import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mat } from "./WorldModels";

const NATURE_ROOT = "/assets/kenney-nature";

/** Remapped Kenney parts ready for InstancedMesh batching. */
export interface NaturePart {
  geometry: THREE.BufferGeometry;
  material: THREE.MeshLambertMaterial;
  /** Local transform relative to the prop root (feet at y=0). */
  matrix: THREE.Matrix4;
}

export interface NatureTemplate {
  id: string;
  parts: NaturePart[];
  height: number;
}

export type NatureFamily = "tree" | "pine" | "bush" | "rock" | "flower" | "plant";

const DECIDUOUS_FILES = [
  "tree_default.glb",
  "tree_oak.glb",
  "tree_detailed.glb",
  "tree_tall.glb",
  "tree_thin.glb",
  "tree_small.glb",
  "tree_simple.glb",
  "tree_plateau.glb",
  "tree_fat.glb",
  "tree_default_dark.glb",
  "tree_oak_dark.glb",
] as const;

const PINE_FILES = ["mini_tree.glb", "mini_tree_high.glb"] as const;

const BUSH_FILES = [
  "plant_bush.glb",
  "plant_bushDetailed.glb",
  "plant_bushLarge.glb",
  "plant_bushSmall.glb",
] as const;

const ROCK_FILES = [
  "rock_smallA.glb",
  "rock_smallB.glb",
  "rock_smallC.glb",
  "rock_smallD.glb",
  "rock_largeA.glb",
  "rock_largeB.glb",
  "mini_stones.glb",
] as const;

const FLOWER_FILES = [
  "flower_purpleA.glb",
  "flower_purpleB.glb",
  "flower_redA.glb",
  "flower_redB.glb",
  "flower_yellowA.glb",
  "flower_yellowB.glb",
] as const;

const PLANT_FILES = ["mini_plant.glb"] as const;

/** Dogdell park palette targets. */
const PAL = {
  leafLite: 0xa8dc78,
  leaf: 0x86c858,
  leafDeep: 0x6eb048,
  trunk: 0x8d6e63,
  trunkLite: 0xa1887f,
  rock: 0x9e958a,
  rockLite: 0xb8b0a4,
  rockDark: 0x7a7368,
  blossom: 0xff9ec8,
  flowerPink: 0xf8bbd0,
  flowerYellow: 0xffe082,
  flowerLilac: 0xe1bee7,
  flowerPeach: 0xffccbc,
} as const;

let ready = false;
let loadPromise: Promise<void> | null = null;

const catalog: Record<NatureFamily, NatureTemplate[]> = {
  tree: [],
  pine: [],
  bush: [],
  rock: [],
  flower: [],
  plant: [],
};

const mappedMatCache = new Map<string, THREE.MeshLambertMaterial>();

function matMapped(
  color: number,
  map: THREE.Texture | null,
): THREE.MeshLambertMaterial {
  if (!map) {
    return mat(color);
  }
  const key = `${color}:${map.uuid}`;
  let m = mappedMatCache.get(key);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color, map });
    m.userData.shared = true;
    mappedMatCache.set(key, m);
  }
  return m;
}

/** Mild multiply tint so Mini Forest colormaps lean Dogdell-warm. */
function tintForTextured(family: NatureFamily): number {
  switch (family) {
    case "pine":
    case "plant":
      return 0xe8f6c8;
    case "rock":
      return 0xf0e8dc;
    default:
      return 0xffffff;
  }
}

export function natureReady(): boolean {
  return ready;
}

export function natureTemplates(family: NatureFamily): readonly NatureTemplate[] {
  return catalog[family];
}

export function pickNatureTemplate(
  family: NatureFamily,
  variant = 0,
): NatureTemplate | null {
  const list = catalog[family];
  if (list.length === 0) {
    return null;
  }
  const n = list.length;
  return list[((variant % n) + n) % n]!;
}

/** Clone a recolored Kenney prop (for one-off props like fetch bushes / rocks). */
export function cloneNatureProp(family: NatureFamily, variant = 0): THREE.Group | null {
  const template = pickNatureTemplate(family, variant);
  if (!template) {
    return null;
  }
  const group = new THREE.Group();
  group.name = `nature-${template.id}`;
  for (const part of template.parts) {
    const mesh = new THREE.Mesh(part.geometry, part.material);
    mesh.matrix.copy(part.matrix);
    mesh.matrixAutoUpdate = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    group.add(mesh);
  }
  return group;
}

export function loadNatureAssets(): Promise<void> {
  if (ready) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = (async () => {
    const loader = new GLTFLoader();
    const jobs: Promise<void>[] = [
      ...DECIDUOUS_FILES.map((f) => loadOne(loader, f, "tree")),
      ...PINE_FILES.map((f) => loadOne(loader, f, "pine")),
      ...BUSH_FILES.map((f) => loadOne(loader, f, "bush")),
      ...ROCK_FILES.map((f) => loadOne(loader, f, "rock")),
      ...FLOWER_FILES.map((f) => loadOne(loader, f, "flower")),
      ...PLANT_FILES.map((f) => loadOne(loader, f, "plant")),
    ];
    await Promise.all(jobs);
    ready = true;
  })();
  return loadPromise;
}

async function loadOne(
  loader: GLTFLoader,
  file: string,
  family: NatureFamily,
): Promise<void> {
  try {
    const gltf = await loader.loadAsync(`${NATURE_ROOT}/${file}`);
    const root = gltf.scene;
    root.updateMatrixWorld(true);

    // Feet on the ground.
    const box = new THREE.Box3().setFromObject(root);
    if (Number.isFinite(box.min.y)) {
      root.position.y -= box.min.y;
      root.updateMatrixWorld(true);
    }

    const parts: NaturePart[] = [];
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || !obj.geometry) {
        return;
      }
      const srcMat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
      const map =
        srcMat && "map" in srcMat
          ? ((srcMat as THREE.MeshStandardMaterial).map ?? null)
          : null;
      const srcColor =
        !map &&
        srcMat &&
        "color" in srcMat &&
        srcMat.color instanceof THREE.Color
          ? srcMat.color.getHex()
          : familyDefaultColor(family, file, obj.name || "");
      const color = map
        ? tintForTextured(family)
        : dogdellifyColor(srcColor, family, file);
      const geometry = obj.geometry.clone();
      geometry.userData.shared = true;
      if (map) {
        map.colorSpace = THREE.SRGBColorSpace;
        map.needsUpdate = true;
      }
      parts.push({
        geometry,
        material: matMapped(color, map),
        matrix: obj.matrixWorld.clone(),
      });
    });

    if (parts.length === 0) {
      return;
    }

    const height = Math.max(0.2, box.max.y - box.min.y);
    catalog[family].push({
      id: file.replace(/\.glb$/i, ""),
      parts,
      height,
    });
  } catch (err) {
    console.warn(`[nature] failed to load ${file}`, err);
  }
}

/**
 * Map Kenney flat colors → Dogdell cozy park palette.
 * Keeps warm lime leaves, brown trunks, soft rocks, pastel blooms.
 */
function dogdellifyColor(hex: number, family: NatureFamily, file: string): number {
  const r = (hex >> 16) & 255;
  const g = (hex >> 8) & 255;
  const b = hex & 255;
  const lum = (r + g + b) / 3;

  if (family === "flower") {
    if (file.includes("yellow")) {
      return PAL.flowerYellow;
    }
    if (file.includes("purple")) {
      return g > 140 && b > 140 ? PAL.leaf : PAL.flowerLilac;
    }
    if (file.includes("red")) {
      // Stem vs bloom: greener → leaf, pinker → bloom.
      if (g > r - 10 && b > r - 40) {
        return PAL.leaf;
      }
      return PAL.flowerPink;
    }
  }

  if (family === "rock") {
    if (r > g + 25 && r > b + 25) {
      return PAL.rockLite;
    }
    if (lum < 120) {
      return PAL.rockDark;
    }
    if (lum > 180) {
      return PAL.rockLite;
    }
    return PAL.rock;
  }

  // Near-white accents: blossoms only on flowers/bushes - trees stay leafy.
  // (Rocks already returned above.)
  if (r > 220 && g > 220 && b > 220) {
    if (family === "flower" || family === "bush") {
      return PAL.blossom;
    }
    return PAL.leafLite;
  }

  // Teal / cyan Kenney foliage (high G+B, low R).
  const isTeal = g > 90 && b > r + 15;
  const isGreen = g > r + 12 && g > b + 8;
  if (isTeal || isGreen) {
    if (lum > 200) {
      return PAL.leafLite;
    }
    if (lum > 150) {
      return PAL.leaf;
    }
    return PAL.leafDeep;
  }

  // Peach / tan / brown wood.
  if (r > g && r >= b - 10 && g > 80) {
    return lum > 180 ? PAL.trunkLite : PAL.trunk;
  }

  // Pink / coral flower bits on mixed meshes.
  if (r > 180 && g < 170 && b < 180) {
    return family === "bush" ? PAL.blossom : PAL.flowerPeach;
  }

  // Cool grey / lavender Mini Forest rocks that landed elsewhere.
  if (family === "pine" || family === "tree") {
    if (r > 140 && g < 130) {
      return PAL.trunk;
    }
  }

  if (b > r + 20 && b > g) {
    return PAL.rock;
  }

  return family === "bush" || family === "plant" ? PAL.leaf : PAL.trunk;
}

/** When GLBs use textures (Mini Forest), guess role from name / family. */
function familyDefaultColor(family: NatureFamily, file: string, meshName: string): number {
  const n = `${file} ${meshName}`.toLowerCase();
  if (family === "rock" || n.includes("stone") || n.includes("rock")) {
    return PAL.rock;
  }
  if (n.includes("trunk") || n.includes("wood") || n.includes("bark")) {
    return PAL.trunk;
  }
  if (family === "flower") {
    if (n.includes("yellow")) return PAL.flowerYellow;
    if (n.includes("purple")) return PAL.flowerLilac;
    if (n.includes("red")) return PAL.flowerPink;
    return PAL.flowerPeach;
  }
  if (family === "pine" || family === "tree" || family === "bush" || family === "plant") {
    if (n.includes("trunk") || n.includes("stem")) return PAL.trunk;
    return PAL.leaf;
  }
  return PAL.leaf;
}
